import { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setUserHeaderLastVersion,
  setClosetHeaderLastVersion,
} from "@actions/user-headers-actions";
import {
  getProductsLiked,
  likeProduct,
  dislikeProduct,
  getProductsRented,
  postFeedbackRating,
} from "@actions/user-actions";
import subscriptionStatus from "@utils/subscriptionStatus";
import catalogContexts from "@utils/constants/catalogContexts";
import sizeReferenceDisplay from "@utils/constants/sizeReferenceDisplay";

export function useCurrentUser() {
  return useSelector((state) => state.user.currentUser);
}

export function useIsPaymentWithStripeFormMandatory() {
  const defaultPaymentMethod = useSelector(
    (state) => state.user.currentUser?.default_payment_method,
  );
  if (!defaultPaymentMethod) return true;

  return defaultPaymentMethod?.type === "sepa_debit";
}

export function useUserHeaderLastVersion() {
  return useSelector((state) => state.user.userHeaderLastVersion);
}

export function useClosetHeaderLastVersion() {
  return useSelector((state) => state.user.closetHeaderLastVersion);
}

export function useSubscriptionStatus() {
  const subscription = useSelector((state) => state.user.currentUser?.subscription);

  return subscriptionStatus(subscription);
}

export function useProductsLiked() {
  return useSelector((state) => state.user.productsLiked);
}

export function useProductsRented() {
  return useSelector((state) => state.user.productsRented);
}

export function useUserHasInternalTools() {
  return useSelector((state) => state.user.currentUser?.lc_internal);
}

export function useSubscriptionEligibleTo() {
  const user = useSelector((state) => state.user.currentUser);

  if (user?.subscription?.plan_price?.plan?.unlimited) return "next_closet";
  if (!user.current_period_closet_exists) return "next_closet";
  if (user.unlock_early_available) return "unlock_early";

  return "no_next_closet_no_unlock_early";
}

export function useSetUserHeaderLastVersion() {
  const dispatch = useDispatch();

  return (userHeaderLastVersion) => dispatch(setUserHeaderLastVersion(userHeaderLastVersion));
}

export function useSetClosetHeaderLastVersion() {
  const dispatch = useDispatch();

  return (closetHeaderLastVersion) => dispatch(setClosetHeaderLastVersion(closetHeaderLastVersion));
}

export function useLikeProduct() {
  const dispatch = useDispatch();

  return (product) => dispatch(likeProduct(product));
}

export function useDislikeProduct() {
  const dispatch = useDispatch();

  return (productId) => dispatch(dislikeProduct(productId));
}

export function useGetProductsLiked() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(getProductsLiked());
  }, [dispatch]);
}

export function useGetProductsRented() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(getProductsRented());
  }, [dispatch]);
}

export function useCanShowSubscriptionIncentives(catalogContext) {
  const user = useSelector((state) => state?.user?.currentUser);

  return !user && catalogContext === catalogContexts.swap;
}

export function useSizeReferenceDisplay() {
  const user = useSelector((state) => state?.user?.currentUser);

  return sizeReferenceDisplay[user?.size_reference_display] || sizeReferenceDisplay.FR;
}

export function usePostFeedbackRating() {
  const dispatch = useDispatch();
  const closetId = useSelector((state) => state.customizableCloset?.currentCloset?.id);

  return async (rating) => dispatch(postFeedbackRating(rating, closetId));
}
